import { Check } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Switch } from "@tokenterminal/core/ui/input/switch/Switch"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { useState } from "react"
import { twMerge } from "tailwind-merge"
import { BASE_PATH } from "@/constants/app.mjs"
import MarketingHeading from "../common/MarketingHeading"

// export const pricingCard = style([
//   cardStyle,
//   atoms({
//     borderRadius: "5x",
//     padding: "6x",
//     gap: "2x",
//   }),
//   {
//     background: "#121212",
//     border: "1px solid #212121",
//     flexGrow: 1,
//     "@media": {
//       [`(max-width: ${breakpoints.tablet}px)`]: {
//         width: "100%",
//       },
//     },
//   },
// ])
// export const pricingCardFeature = style([
//   atoms({
//     gap: "3x",
//   }),
//   {
//     display: "flex",
//     alignItems: "center",
//   },
// ])

// export const pricingCardWrapper = style([
//   {
//     margin: "auto",
//   },
// ])

const pricingCard =
  "bg-bg-surface border border-solid border-border-default rounded-2xl p-6 gap-2 flex-1 w-full lg:w-auto"
const pricingCardFeature = ""
const pricingCardWrapper = ""

const CheckIcon = () => <Check size={14} weight="bold" className="text-fg-emerald" />

const FeatureLine = ({ active = true, name }: { active?: Boolean; name: String }) => {
  if (active) {
    return (
      <div
        className={twMerge(
          "flex items-center gap-2 text-sm text-fg-default font-normal",
          pricingCardFeature,
        )}
      >
        <CheckIcon />
        <span>{name}</span>
      </div>
    )
  } else {
    return (
      <div
        className={twMerge(
          "flex items-center gap-2 text-sm text-fg-secondary font-normal pl-7 line-through",
          pricingCardFeature,
        )}
      >
        <span>{name}</span>
      </div>
    )
  }
}

const pricingPlans: Array<{
  name: string
  monthlyPrice: number
  annualPrice: number
  description: string
  features: Array<{ name: string; active: boolean }>
  cta: JSX.Element
}> = [
  {
    name: "Free",
    monthlyPrice: 0,
    annualPrice: 0,
    description: "For hobbyists and emerging investors",
    features: [
      {
        name: "Full historical data",
        active: true,
      },
      {
        name: "3 custom dashboards",
        active: true,
      },
      {
        name: "Sheets and Excel plugin",
        active: false,
      },
      {
        name: "Access to MCP",
        active: false,
      },
      {
        name: "CSV data exports",
        active: false,
      },
      {
        name: "Team access",
        active: false,
      },
    ],
    cta: (
      <LinkButton className="w-full" href={`${BASE_PATH}/explorer/auth/signup`}>
        Sign up for Free
      </LinkButton>
    ),
  },
  {
    name: "Pro",
    monthlyPrice: 350,
    annualPrice: 292,
    description: "For professional investors and teams",
    features: [
      {
        name: "Full historical data",
        active: true,
      },
      {
        name: "Unlimited custom dashboards",
        active: true,
      },
      {
        name: "Access to MCP",
        active: true,
      },
      {
        name: "Sheets and Excel plugin",
        active: true,
      },
      {
        name: "CSV data exports",
        active: true,
      },
      {
        name: "Team access",
        active: true,
      },
    ],
    cta: (
      <LinkButton color="success" className="w-full" href={`${BASE_PATH}/explorer/auth/signup`}>
        Start 7 day trial
      </LinkButton>
    ),
  },
  {
    name: "API",
    monthlyPrice: 999,
    annualPrice: 834,
    description: "For developers and builders",
    features: [
      {
        name: "Everything in Pro",
        active: true,
      },
      {
        name: "REST API access to all data",
        active: true,
      },
      {
        name: "250,000 requests/day",
        active: true,
      },
      {
        name: "Status page for API uptime",
        active: true,
      },
      {
        name: "Discounts for startups",
        active: true,
      },
    ],
    cta: (
      <LinkButton
        className="w-full"
        href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
      >
        Book a demo
      </LinkButton>
    ),
  },
  {
    name: "Datashare",
    monthlyPrice: 4999,
    annualPrice: 3999,
    description: "For institutional teams and platforms",
    features: [
      {
        name: "Everything in API",
        active: true,
      },
      {
        name: "Raw blockchain data access",
        active: true,
      },
      {
        name: "Data sharing in BigQuery and Snowflake",
        active: true,
      },
      {
        name: "Dedicated support & SLA",
        active: true,
      },
    ],
    cta: (
      <LinkButton
        className="w-full"
        href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
      >
        Book a demo
      </LinkButton>
    ),
  },
]

const PricingTable = () => {
  const [showAnnual, setShowAnnual] = useState(false)

  const toggleAnnual = () => {
    setShowAnnual(!showAnnual)
  }

  return (
    <div className={twMerge("flex flex-col gap-8 w-full", pricingCardWrapper)}>
      <div className="flex justify-center items-center gap-4 w-full">
        <label className="flex items-center gap-2" htmlFor="annual">
          <span className="text-fg-default text-base">Monthly</span>
          <Switch name="annual" id="annual" onChange={toggleAnnual} isSelected={showAnnual} />
          <span className="text-fg-default text-base">Annual</span>
          <Badge
            size="small"
            variant="outline"
            className="ml-2 !text-fg-secondary !border-border-neutral"
          >
            Save 15%
          </Badge>
        </label>
      </div>
      <div className="flex flex-col gap-4">
        <div className={twMerge("flex justify-center gap-4 w-full flex-col md:flex-row")}>
          {pricingPlans.slice(0, 2).map((plan) => (
            <div key={plan.name} className={twMerge("flex flex-col", pricingCard)}>
              <div className="flex flex-row justify-between items-baseline gap-3 mb-2">
                <MarketingHeading level={3}>{plan.name}</MarketingHeading>
                {showAnnual && plan.name !== "Free" && plan.name !== "API" ? (
                  <span className="text-xl text-fg-default font-mono">
                    {`$${plan.annualPrice}`}&nbsp;
                    <span className="text-xl text-neutral-500 line-through">{`$${plan.monthlyPrice}`}</span>
                  </span>
                ) : (
                  <span className="text-xl text-neutral-500 font-mono">{`$${plan.monthlyPrice}`}</span>
                )}
              </div>
              <div className="text-fg-secondary mb-4">{plan.description}</div>
              <div className="flex flex-col gap-3 mb-4">
                {plan.features.map((feature) => (
                  <FeatureLine key={feature.name} name={feature.name} active={feature.active} />
                ))}
              </div>
              <div className="mt-auto pt-2">{plan.cta}</div>
            </div>
          ))}
        </div>
        <div className={twMerge("flex justify-center gap-4 w-full flex-col md:flex-row")}>
          {pricingPlans.slice(2, 4).map((plan) => (
            <div key={plan.name} className={twMerge("flex flex-col", pricingCard)}>
              <div className="flex flex-row justify-between items-baseline gap-3 mb-2">
                <MarketingHeading level={3}>{plan.name}</MarketingHeading>
                <span className="text-lg text-neutral-500 font-mono">Custom pricing</span>
              </div>
              <div className="text-fg-secondary mb-4">{plan.description}</div>
              <div className="flex flex-col gap-3 mb-4">
                {plan.features.map((feature) => (
                  <FeatureLine key={feature.name} name={feature.name} active={feature.active} />
                ))}
              </div>
              <div className="mt-auto pt-2">{plan.cta}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default PricingTable
